/**
 * Pure, framework-free helpers for the subscription checkout flow.
 *
 * Split out of useSubscription.js so the money-path decision logic behind
 * the QA-016 / SUB-012 fix -- same-tab checkout redirect instead of a
 * popup-blocker-prone `window.open`, plus a no-card pre-check before a paid
 * upgrade/downgrade -- can be unit tested without mounting React, mocking
 * `window.location`, or standing up react-query.
 *
 * See: audits/FINDINGS-REGISTER.md (SUB-012), audits/QA-BUG-CATALOG.md (QA-016).
 */

export const SUBSCRIPTION_ACTIONS = {
  SUBSCRIBE: "SUBSCRIBE",
  UPGRADE: "UPGRADE",
  DOWNGRADE: "DOWNGRADE",
  CANCEL: "CANCEL",
  ACTIVE: "ACTIVE",
};

/**
 * A purchase/change-subscription mutation response may carry a Stripe
 * Checkout (or SCA-challenge) redirect URL at `res.data.data.url`. The old
 * code opened this with `window.open(url, "_blank")`, which a popup blocker
 * silently kills (QA-016): the main tab just sits there and the user never
 * reaches Stripe. This function is the pure "is there a URL to redirect to"
 * decision; the caller performs the actual `window.location.href = url`
 * side effect, which -- being a same-tab navigation, not a new window --
 * cannot be blocked by a popup blocker in any browser.
 *
 * Returns the URL string, or `null` if there is nothing to redirect to.
 */
export const getCheckoutRedirectUrl = (response) => {
  const url = response?.data?.data?.url;
  return typeof url === "string" && url.length > 0 ? url : null;
};

/**
 * `handleChangeSubscription` only ever redirects for an UPGRADE (a
 * downgrade never needs a Stripe redirect). Composes with
 * `getCheckoutRedirectUrl` so the "is this an upgrade" gate and the
 * "does the response have a url" check both live in one testable place.
 */
export const getPostChangeRedirectUrl = (response, isUpgrade) =>
  isUpgrade ? getCheckoutRedirectUrl(response) : null;

/**
 * Whether a subscription action can ever need an existing default payment
 * method already on file:
 *  - SUBSCRIBE (first paid plan, from Free) goes through a fresh Stripe
 *    Checkout Session, which collects card details itself.
 *  - CANCEL (downgrade to Free) charges nothing.
 *  - UPGRADE and DOWNGRADE (moving between two paid tiers) charge the
 *    account's existing default card directly -- no `payment_method` is
 *    sent (see SUB-012) -- so one must already exist on file.
 */
export const actionRequiresExistingCard = (action) =>
  action === SUBSCRIPTION_ACTIONS.UPGRADE ||
  action === SUBSCRIPTION_ACTIONS.DOWNGRADE;

/**
 * The no-card pre-check (SUB-012): decide whether to block the confirm
 * action *before* ever hitting the server, instead of letting a paid
 * upgrade/downgrade fail server-side with a bare "Failed to process
 * subscription" toast because Stripe had no default card to charge.
 *
 * Deliberately does NOT block while the card list is still loading -- an
 * "unknown yet" state should never read as "definitely no card" (that would
 * flash the button disabled for every user on every open, including ones
 * who do have a card). Only a *confirmed* absence of a default card blocks.
 */
export const shouldBlockForMissingCard = ({
  action,
  hasDefaultPaymentMethod,
  isCardsLoading = false,
}) => {
  if (!actionRequiresExistingCard(action)) return false;
  if (isCardsLoading) return false;
  return !hasDefaultPaymentMethod;
};

/**
 * Error code attached to the Error thrown/passed to `onError` when the
 * pre-check above blocks a confirm -- lets the UI show a specific,
 * actionable message ("add a payment method") instead of the generic
 * "Failed to process subscription" toast, and lets callers/tests
 * distinguish this from a real server-side failure without parsing text.
 */
export const NO_DEFAULT_PAYMENT_METHOD = "NO_DEFAULT_PAYMENT_METHOD";

export const MISSING_CARD_MESSAGE =
  "Please add a payment method before upgrading or changing your plan.";

/**
 * Builds the Error object passed to a checkout action's `onError` callback
 * when `shouldBlockForMissingCard` blocks the confirm. A real `Error` (not a
 * plain object) so it behaves like every other error this codebase passes
 * through `onError`, but carries a `.code` an error handler can switch on.
 */
export const createMissingCardError = () => {
  const error = new Error(MISSING_CARD_MESSAGE);
  error.code = NO_DEFAULT_PAYMENT_METHOD;
  return error;
};
